/** Presentation only. Never use these labels as API, database, or signed-artifact identities. */
export const RETURN_COPY = {
  singular: "Return",
  plural: "Returns",
  new: "New Return",
  create: "Create Return",
  open: "Open Return",
  review: "Review Return",
  assign: "Assign Return",
  active: "Active Returns",
  openReturns: "Open Returns",
  recent: "Recent Returns",
  mine: "My Returns",
  your: "Your Returns",
  name: "Return name",
  id: "Return ID",
  status: "Return status",
  dashboard: "Return Dashboard",
  details: "Return Details",
  timeline: "Return Timeline",
  queue: "Return Queue",
  management: "Return Management",
  workflow: "Return Workflow",
  metrics: "Return Metrics",
  activity: "Return Activity",
  evidence: "Return Evidence",
  audit: "Return Audit",
  first: "Create your first return",
  none: "No returns found",
  noneAssigned: "You do not have any returns",
  noSelection: "No return selected",
  activeSelection: "Active return",
  search: "Search screens, returns and rules",
  searchPlaceholder: "Search screens, returns, rules…",
  empty: "No returns yet. Create a synthetic return to begin.",
} as const;

export const RETURN_NOMENCLATURE = {
  entity: "Return",
  identifier: "Return ID",
  formFamily: "Return type",
  taxYear: "Tax year",
  filingType: "Filing type",
  // Vocabulary only, not implemented return types or expanded release capabilities.
  formFamilies: ["1040", "1120-S", "1065", "1041", "990"],
  filingTypes: ["Original", "Extension", "Amendment"],
} as const;

function matchingCase(original: string, replacement: string): string {
  if (original === original.toUpperCase()) return replacement.toUpperCase();
  return /^[A-Z]/.test(original)
    ? replacement[0].toUpperCase() + replacement.slice(1)
    : replacement;
}

/** For application-authored prose only, not user names, document text, raw JSON or source IDs. */
export function taxText(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  // Protect technical identifiers, paths, URLs, code, and English uses unrelated to a return.
  return text.replace(
    /`[^`]*`|https?:\/\/\S+|\/[\w./?=&%:#-]+|\b(?:case[_-][A-Za-z0-9_-]+|cases[_-][A-Za-z0-9_-]+)\b|\bcase[- ](?:in)?sensitive\b|\bin case\b|\bcase law\b|\btest cases?\b|\bcases?\b/gi,
    (match) => {
      if (
        /^case-(?:specific|level|wide|based|related|linked|bound|scoped|owned|centric)$/i.test(
          match,
        )
      )
        return matchingCase(match, "return" + match.slice(4));
      if (/^test cases?$/i.test(match))
        return matchingCase(
          match,
          /cases$/i.test(match) ? "test scenarios" : "test scenario",
        );
      if (/^cases?$/i.test(match))
        return matchingCase(
          match,
          /^cases$/i.test(match) ? "returns" : "return",
        );
      return match;
    },
  );
}

export function permissionLabel(permission: string): string {
  return (
    (
      {
        "case:read": "View returns",
        "case:draft": "Edit return drafts",
      } as Record<string, string>
    )[permission] || permission
  );
}

export function capabilityLabel(capability: string): string {
  return capability === "caseDrafts" ? "Return drafts" : capability;
}

export type ReturnDisplayIndex = ReadonlyMap<string, string>;
type ReturnIdentity = {
  id?: unknown;
  reference?: unknown;
  createdAt?: unknown;
};

/**
 * Number the complete workspace collection, never a filtered table or page.
 * Existing source identity is untouched. Sorting uses immutable creation time,
 * then the original ID as the deterministic tie-breaker. These are workspace
 * display ordinals, not permanent external identifiers; historical membership
 * changes can renumber them. A permanent sequence would require persistence.
 */
export function createReturnDisplayIndex(
  records: readonly ReturnIdentity[],
): Map<string, string> {
  const byId = new Map<string, { id: string; createdAt: number }>();
  for (const record of records) {
    if (
      typeof record.id !== "string" ||
      !record.id ||
      typeof record.createdAt !== "string"
    )
      continue;
    const createdAt = Date.parse(record.createdAt);
    if (!Number.isFinite(createdAt)) continue;
    if (!byId.has(record.id)) byId.set(record.id, { id: record.id, createdAt });
  }
  const ordered = [...byId.values()].sort(
    (a, b) =>
      a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  return new Map(
    ordered.map((record, index) => [
      record.id,
      `RET-${String(index + 1).padStart(6, "0")}`,
    ]),
  );
}

export function displayReturnId(
  record: ReturnIdentity,
  index?: ReturnDisplayIndex,
): string {
  const alias =
    typeof record.id === "string" ? index?.get(record.id) : undefined;
  if (alias) return alias;
  // Legacy numeric references can be relabeled exactly without inventing a number.
  const legacy =
    typeof record.reference === "string"
      ? /^(?:CASE|RET)-(\d+)$/i.exec(record.reference)
      : null;
  if (legacy) return `RET-${legacy[1].padStart(6, "0")}`;
  return "Return ID unavailable";
}

/** Labels for internal activity types; the stored action value remains verbatim. */
export function activityLabel(value: unknown): string {
  return taxText(
    String(value ?? "Workspace event").replace(/[._-]+/g, " "),
  ).replace(/^./, (char) => char.toUpperCase());
}

/** Translate only known workspace references embedded in application-generated summaries. */
export function returnSystemText(
  value: unknown,
  records: readonly ReturnIdentity[],
  index: ReturnDisplayIndex,
): string {
  let output = taxText(value);
  for (const record of records) {
    if (
      typeof record.reference !== "string" ||
      !record.reference ||
      typeof record.id !== "string"
    )
      continue;
    const alias = index.get(record.id);
    if (!alias) continue;
    const escaped = record.reference.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    output = output.replace(
      new RegExp(`(?<![A-Za-z0-9_-])${escaped}(?![A-Za-z0-9_-])`, "g"),
      alias,
    );
  }
  return output;
}
